import {
    SummaryData,
    ScoreDetailBuilder,
    ScoreDetailBuilderConstructor,
    SummaryDetail,
    SummaryDetailItem,
} from "src/context/ScoreDetail/ScoreDetailBuilder";
import { ProductSummary } from "./useProductSummaryData";
import { Product } from "src/api/client-api/products/useGetOneProduct";
import { ScoreChartValues } from "src/components/ScoreChart/ScoreChart";

export interface ChartValuesWithTotal extends ScoreChartValues {
    totalScore: number;
}

export type ProductSummaryType = "product" | "ingredients";
export type ProductMetadata = Pick<Product, "productIngredients">;

type ProductDetailBuilderConstructor = ScoreDetailBuilderConstructor & {
    summaryData?: ProductSummary;
    productMetadata: ProductMetadata;
};

export class ProductDetailBuilder extends ScoreDetailBuilder {
    summaryData: ProductSummary | null;
    productMetadata: ProductMetadata;
    scoreId: number;

    public constructor(options: ProductDetailBuilderConstructor) {
        const { summaryData, productMetadata } = options;
        super(options);

        const { scoreId } = options;
        this.scoreId = scoreId;

        this.summaryData = summaryData === undefined ? null : summaryData;
        this.productMetadata = productMetadata;
    }

    public getSummary = (): SummaryData | null => {
        if (this.summaryData === null) return null;

        const allSummaryScores = this.summaryData.summaryScores?.[this.scoreId];
        if (allSummaryScores === undefined) return null;

        const globalSummary = ProductDetailBuilder.drilldownDimension(allSummaryScores?.dimensions, "components");
        const ingredientsSummary = ProductDetailBuilder.drilldownDimension(allSummaryScores?.dimensions, "ingredients");

        const productKey = "product" as const;
        const ingredientKey = "ingredients" as const;
        if (globalSummary === null) return null;
        const globalProductValue = globalSummary[productKey];
        const globalIngredients = globalSummary[ingredientKey];
        const summary = [
            {
                id: productKey,
                label: globalProductValue?.label,
                score: globalProductValue?.score,
            },
            {
                id: ingredientKey,
                label: globalIngredients?.label,
                score: globalIngredients?.score,
            },
        ];
        const globalTotalScore = (globalProductValue?.score ?? 0) + (globalIngredients?.score ?? 0);

        const summaryDetail: SummaryDetail = {};
        if (ingredientsSummary === null) return null;
        const ingredientDetails = this.productMetadata.productIngredients?.map((prodIng) => {
            const ingId = prodIng.ingredient.id;
            const ingData = ingredientsSummary[ingId];
            if (ingData === undefined) {
                const noScore: SummaryDetailItem = {
                    id: ingId,
                    label: prodIng.ingredient.name,
                    score: null,
                };
                return noScore;
            }
            const res: SummaryDetailItem = {
                id: ingId,
                label: prodIng.ingredient.name,
                score: ingData.score,
            };
            return res;
        });
        if (ingredientDetails !== undefined) {
            summaryDetail[ingredientKey] = ingredientDetails;
        }

        const productSummary = {
            totalScore: globalTotalScore,
            summary,
            detail: summaryDetail,
        };
        return productSummary;
    };

    public getSummarytoChartValue = (productSummary?: SummaryData): ChartValuesWithTotal | null => {
        const summary = productSummary ? productSummary : this.getSummary();
        if (summary === null) return null;

        const acc: ChartValuesWithTotal = { labels: [], values: [], totalScore: 0 };
        const chartValues = summary.summary.reduce((prevAcc, summaryItem) => {
            prevAcc.labels.push(summaryItem.label);
            const score = summaryItem.score ?? 0;

            prevAcc.values.push(score);
            prevAcc.totalScore += score;
            return prevAcc;
        }, acc);

        return chartValues;
    };
}
